import { useState } from 'react';
import { useMailProvider } from '../MailProviderContext';
import { useClientStore } from '../../../lib/stores/clientStore';
import { useLeadNoteStore } from '../../../lib/stores/leadNoteStore';
import { useProjectStatusUpdateStore } from '../../../lib/stores/projectStatusUpdateStore';
import { useContactCompanyLinkStore } from '../../../lib/stores/contactCompanyLinkStore';
import { useApp } from '../../AppProvider';
import { createFromProspect } from '../../../lib/mail/createFromProspect';
import { linkExists } from '../../../lib/mail/prospectLookup';
import { findPersonByEmail } from '../../../lib/mail/threadPersonLookup';
import { decidePersonPrompt, type PersonPromptDecision } from '../../../lib/mail/threadLink';
import type { SearchRow, LinkTarget } from './ExistingSearchPanel';
import type { useProspectDraft } from './useProspectDraft';
import type { LinkableThread } from '../MailLinkEntityDrawer';

export interface PersonPromptState {
  decision: Exclude<PersonPromptDecision, 'none'>;
  company: { id: string; name: string };
  existingPerson: { id: string; business_name: string } | null;
}

interface Args {
  threads: LinkableThread[];
  organizationId: string;
  userId: string;
  target: LinkTarget;
  draft: ReturnType<typeof useProspectDraft>;
  pipelineId: string;
  buildPayload: (t: LinkableThread) => Record<string, unknown> & { fromEmail: string };
  /** Chiude il pannello con l'animazione di uscita. */
  dismiss: () => void;
}

/**
 * Azioni del pannello "Collega a": collegamento a un'entita' esistente, proposta della
 * persona su un'azienda gia' in anagrafica, e creazione guidata.
 *
 * Vivono qui e non nel componente perche' sono la parte che cresce: tenerle separate
 * lascia leggibile la struttura dell'interfaccia.
 */
export function useLinkActions(args: Args) {
  const { threads, organizationId, userId, target, draft, pipelineId, buildPayload, dismiss } = args;
  const { toast } = useApp();
  const { useStore, provider } = useMailProvider();
  const linkThreadToClient = useStore(s => s.linkThreadToClient);
  const createClient = useClientStore(s => s.createClient);
  const createEmailNote = useLeadNoteStore(s => s.createEmailNote);
  const createEmailStatusUpdate = useProjectStatusUpdateStore(s => s.createEmailStatusUpdate);
  const linkPersonToCompany = useContactCompanyLinkStore(s => s.linkPersonToCompany);

  const [linking, setLinking] = useState(false);
  const [prompt, setPrompt] = useState<PersonPromptState | null>(null);

  const single = threads.length === 1;
  const leadSource = provider === 'microsoft' ? 'outlook' : 'gmail';

  const linkAll = async (row: SearchRow) => {
    for (const t of threads) {
      const payload = buildPayload(t);
      if (target === 'client') {
        await linkThreadToClient(organizationId, userId, t.threadId, row.id, {
          id: row.id, business_name: row.primary, email: row.email ?? null,
        });
        await createEmailNote({ leadId: row.id }, payload as never);
      } else if (target === 'deal') {
        if (row.clientId) {
          await linkThreadToClient(organizationId, userId, t.threadId, row.clientId, {
            id: row.clientId, business_name: row.secondary || row.primary, email: null,
          });
        }
        await createEmailNote({ dealId: row.id }, payload as never);
      } else {
        await createEmailStatusUpdate(row.id, payload as never);
      }
    }
  };

  /**
   * Il thread e` collegato a un'azienda: se chi scrive non e` in anagrafica (o non e`
   * collegato a quell'azienda) il pannello resta aperto e lo propone, invece di
   * costringere a rifare tutto dalla scheda cliente.
   */
  const maybeOfferPerson = async (row: SearchRow): Promise<boolean> => {
    if (!single || target !== 'client' || row.entityType !== 'company') return false;
    const senderEmail = buildPayload(threads[0]).fromEmail || null;
    const existingPerson = senderEmail ? await findPersonByEmail(organizationId, senderEmail) : null;
    const alreadyLinked = existingPerson ? await linkExists(existingPerson.id, row.id) : false;

    const decision = decidePersonPrompt({
      senderEmail,
      existingPersonId: existingPerson?.id ?? null,
      alreadyLinked,
    });
    if (decision === 'none') return false;

    setPrompt({
      decision,
      company: { id: row.id, name: row.primary },
      existingPerson: existingPerson
        ? { id: existingPerson.id, business_name: existingPerson.business_name }
        : null,
    });
    return true;
  };

  const handleSelect = async (row: SearchRow) => {
    setLinking(true);
    try {
      await linkAll(row);
      const what = single ? 'Thread collegato' : `${threads.length} thread collegati`;
      toast.success(`${what} a ${row.primary}`);
      const staysOpen = await maybeOfferPerson(row);
      if (!staysOpen) dismiss();
    } catch (e) {
      console.error('[MailLinkEntityDrawer] link error:', e);
      toast.error('Errore nel collegamento');
    } finally {
      setLinking(false);
    }
  };

  /** Crea (o collega soltanto) la persona come referente dell'azienda gia' collegata. */
  const handleConfirmPerson = async () => {
    if (!prompt) return;
    setLinking(true);
    try {
      const created = await createFromProspect(
        {
          // 'both' e non 'person': l'azienda esiste gia`, quindi non viene creata, ma
          // deve restare nel piano perche' e` lei il capo del collegamento. Con 'person'
          // la persona nascerebbe slegata.
          kind: 'both',
          company: {},
          person: draft.person,
          extraPeople: [],
          pipelineId: null,
          leadSource,
          existing: {
            company: { id: prompt.company.id, business_name: prompt.company.name },
            companyMatchedBy: 'domain',
            person: prompt.existingPerson,
          },
          websiteHint: null,
        },
        {
          createClient: (data) => createClient(data as never, { asReferente: true }),
          linkPersonToCompany,
          linkExists,
        },
      );

      if (created.personId) {
        await createEmailNote({ leadId: created.personId }, buildPayload(threads[0]) as never);
      }
      toast.success(created.personCreated ? 'Persona creata e collegata' : 'Persona collegata');
      setPrompt(null);
      dismiss();
    } catch (e) {
      console.error('[MailLinkEntityDrawer] person prompt error:', e);
      toast.error('Errore nella creazione della persona');
    } finally {
      setLinking(false);
    }
  };

  const handleCreateAndLink = async () => {
    if (!single) return;
    const wantsCompany = draft.kind === 'company' || draft.kind === 'both';
    const wantsPerson = draft.kind === 'person' || draft.kind === 'both';

    if (wantsPerson && !draft.person.business_name.trim() && !draft.person.email.trim()) {
      toast.error('Inserisci almeno nome o email della persona');
      return;
    }
    if (wantsCompany && !draft.companyValues.business_name?.trim()) {
      toast.error('Inserisci la ragione sociale');
      return;
    }

    setLinking(true);
    let created: Awaited<ReturnType<typeof createFromProspect>> | null = null;
    try {
      created = await createFromProspect(
        {
          kind: draft.kind,
          company: draft.companyValues,
          person: draft.person,
          extraPeople: draft.participants.filter(p => draft.extraPeople.has(p.email)),
          pipelineId: pipelineId || null,
          leadSource,
          existing: draft.matches,
          websiteHint: draft.selectedSite,
        },
        {
          createClient: (data, options) =>
            createClient(data as never, options?.asReferente ? { asReferente: true } : undefined),
          linkPersonToCompany,
          linkExists,
        },
      );

      // Il thread si aggancia all'azienda quando c'e', altrimenti alla persona.
      const threadTarget = created.companyId || created.personId;
      const payload = buildPayload(threads[0]);
      if (threadTarget) {
        await linkThreadToClient(organizationId, userId, threads[0].threadId, threadTarget, {
          id: threadTarget,
          business_name: created.label,
          email: (threadTarget === created.companyId ? draft.companyValues.email : draft.person.email) || null,
        });
      }
      if (created.companyId) {
        await createEmailNote({ leadId: created.companyId }, { ...payload, pipelineId: pipelineId || undefined } as never);
      }
      if (created.personId && created.personId !== created.companyId) {
        await createEmailNote({ leadId: created.personId }, payload as never);
      }

      const nothingCreated = !created.companyCreated && !created.personCreated;
      toast.success(nothingCreated ? `Collegato a ${created.label}` : `Creato e collegato: ${created.label}`);
      dismiss();
    } catch (e) {
      console.error('[MailLinkEntityDrawer] create+link error:', e);
      // Esito parziale dichiarato: se l'azienda e` stata creata resta in anagrafica.
      toast.error(
        created?.companyCreated
          ? "Azienda creata, ma il collegamento non e' riuscito. Completare dalla scheda azienda."
          : 'Errore nella creazione',
      );
    } finally {
      setLinking(false);
    }
  };

  return { linking, prompt, setPrompt, handleSelect, handleConfirmPerson, handleCreateAndLink };
}
